import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { DeviceCard } from '@/components/common/DeviceCard';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { useDevices } from '@/hooks/useDevices';

export function Devices() {
  useRealtimeDevices();
  const [platformFilter, setPlatformFilter] = useState<'all' | 'ios' | 'android'>('all');
  const [osVersionFilter, setOsVersionFilter] = useState<'all' | string>('all');
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showOsVersionDropdown, setShowOsVersionDropdown] = useState(false);

  const { data, isLoading, error } = useDevices({
    platform: platformFilter !== 'all' ? platformFilter : undefined,
    osVersion: osVersionFilter !== 'all' ? osVersionFilter : undefined,
  });

  const devices = data?.devices || [];
  const onlineCount = devices.filter(d => d.status === 'online').length;

  const uniqueOsVersions = useMemo(() =>
    Array.from(new Set(devices.map(d => d.osVersion))),
    [devices]
  );

  useEffect(() => {
    const handleClickOutside = () => {
      setShowPlatformDropdown(false);
      setShowOsVersionDropdown(false);
    };

    if (showPlatformDropdown || showOsVersionDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showPlatformDropdown, showOsVersionDropdown]);

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Dispositivos' }]}>
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar dispositivos
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar a lista de dispositivos. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Dispositivos' }]}>
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-3xl font-semibold text-text-title">Dispositivos Conectados</h2>
              <p className="text-base text-text-body">
                <span className="rounded-full bg-online/20 px-2 py-0.5 text-sm font-semibold tabular-nums text-online">
                  {onlineCount} online
                </span>
              </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Platform Filter */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPlatformDropdown(!showPlatformDropdown);
                  }}
                  className="flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-lg border border-border bg-card px-4 pr-2 text-sm font-medium text-text-title shadow-sm hover:bg-muted/50"
                >
                  Plataforma: {platformFilter === 'all' ? 'Todas' : platformFilter === 'ios' ? 'iOS' : 'Android'}
                  <ChevronDown size={18} />
                </button>
                {showPlatformDropdown && (
                  <div className="absolute left-0 z-10 mt-2 w-full rounded-lg border border-border bg-card shadow-lg">
                    <button
                      onClick={() => {
                        setPlatformFilter('all');
                        setShowPlatformDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                    >
                      Todas
                    </button>
                    <button
                      onClick={() => {
                        setPlatformFilter('ios');
                        setShowPlatformDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                    >
                      iOS
                    </button>
                    <button
                      onClick={() => {
                        setPlatformFilter('android');
                        setShowPlatformDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                    >
                      Android
                    </button>
                  </div>
                )}
              </div>

              {/* OS Version Filter */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowOsVersionDropdown(!showOsVersionDropdown);
                  }}
                  className="flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-lg border border-border bg-card px-4 pr-2 text-sm font-medium text-text-title shadow-sm hover:bg-muted/50"
                >
                  Versão do Sistema: {osVersionFilter === 'all' ? 'Todas' : osVersionFilter}
                  <ChevronDown size={18} />
                </button>
                {showOsVersionDropdown && (
                  <div className="absolute left-0 z-10 mt-2 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                    <button
                      onClick={() => {
                        setOsVersionFilter('all');
                        setShowOsVersionDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                    >
                      Todas
                    </button>
                    {uniqueOsVersions.map((version) => (
                      <button
                        key={version}
                        onClick={() => {
                          setOsVersionFilter(version);
                          setShowOsVersionDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                      >
                        {version}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Device Cards Grid */}
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {isLoading ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <RefreshCw className="size-8 animate-spin text-primary" />
            </div>
          ) : devices.length > 0 ? (
            devices.map((device) => (
              <DeviceCard
                key={device.id}
                id={device.id}
                name={device.model}
                user={device.userName}
                status={device.status}
                platform={device.platform === 'ios' ? 'iOS' : 'Android'}
                appVersion={device.appVersion}
                osVersion={device.osVersion}
                lastActivity={device.lastActivity}
                sessions={device.sessions}
                errors={device.errors}
                crashes={device.crashes}
              />
            ))
          ) : (
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="text-center">
                <p className="mb-2 text-lg font-medium text-text-title">
                  Ainda não houve conexões de aparelhos do app
                </p>
                <p className="text-sm text-text-secondary">
                  Os dispositivos aparecerão aqui assim que fizerem a primeira conexão
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
